import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaFileExcel, FaPrint, FaArrowLeft } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AccountantSocPaymentList = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const componentRef = useRef();

    const handlePrint = () => {
        window.print();
    };

    useEffect(() => {
        setLoading(true);
        const societiesRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        onValue(societiesRef, (snapshot) => {
            if (snapshot.exists()) {
                const societiesData = snapshot.val();
                const allPayments = [];

                Object.entries(societiesData).forEach(([userId, user]) => {
                    const paymentNode = user.payments || {};
                    Object.entries(paymentNode).forEach(([type, p]) => {
                        allPayments.push({
                            id: `${userId}-${type}`,
                            userId: userId,
                            userName: user.societyName || 'N/A',
                            mobile: user.mobile || user.chairmanWhatsapp || 'N/A',
                            userType: 'Society',
                            type: type,
                            amount: p.paidAmount || '0.00',
                            transactionId: p.userUpiId || p.transactionId || p.receiptId || 'N/A',
                            date: p.date || 'N/A',
                            rawDate: p.date ? new Date(p.date) : new Date(0)
                        });
                    });
                });

                // Sort by date descending
                allPayments.sort((a, b) => b.rawDate - a.rawDate);
                setPayments(allPayments);
            } else {
                setPayments([]);
            }
            setLoading(false);
        });
    }, []);

    const filteredData = payments.filter(item => 
        item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExport = () => {
        const ws = XLSX.utils.json_to_sheet(filteredData.map((row, i) => ({
            '#': i + 1,
            'संस्थेचे नाव': row.userName,
            'मोबाईल नंबर': row.mobile,
            'पेमेंट प्रकार': row.type,
            'रक्कम': row.amount,
            'ट्रान्झॅक्शन आयडी': row.transactionId,
            'तारीख': row.date
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SocietyPayments");
        XLSX.writeFile(wb, "Society_Payments_Report.xlsx");
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f4f7fe] p-4 md:p-8 font-sans">
            <div className="max-w-[98%] mx-auto bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                
                {/* Header Section */}
                <div className="bg-white px-8 py-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight">मजूर संस्था पेमेंट यादी</h1>
                        <p className="text-gray-400 text-sm font-medium mt-1">
                            ठाणे जिल्हा परिषद / <span className="font-bold">Society Payment List</span>
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={handleExport}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-xs transition-all shadow-sm active:scale-95"
                        >
                            <FaFileExcel /> EXCEL
                        </button>
                        <button 
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded font-bold text-xs transition-all shadow-sm active:scale-95"
                        >
                            <FaPrint /> PRINT
                        </button>
                        <button 
                            onClick={() => navigate('/accountant/dashboard')}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-bold text-xs transition-all shadow-sm active:scale-95"
                        >
                            <FaArrowLeft /> DASHBOARD
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* Search Bar */}
                    <div className="mb-8 flex justify-end">
                        <div className="relative w-full md:w-80">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-3.5" />
                            <input 
                                type="text" 
                                placeholder="Search by name, ID or type..." 
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500/20 outline-none transition"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm" ref={componentRef}>
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[#f8f9fa] border-b border-gray-200">
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center w-16">#</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r">संस्थेचे नाव</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center">मोबाईल नंबर</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center">पेमेंट प्रकार</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center">रक्कम</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center">ट्रान्झॅक्शन आयडी</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-r text-center">तारीख</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center print:hidden">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredData.length > 0 ? (
                                    filteredData.map((row, index) => (
                                        <tr key={row.id} className="hover:bg-indigo-50/20 transition-all font-medium text-gray-700">
                                            <td className="px-6 py-4 border-r text-center text-sm font-black text-gray-300 italic">{index + 1}</td>
                                            <td className="px-6 py-4 border-r text-sm font-bold text-gray-800">{row.userName}</td>
                                            <td className="px-6 py-4 border-r text-center text-sm font-bold text-gray-600">{row.mobile}</td>
                                            <td className="px-6 py-4 border-r text-center text-xs font-bold">
                                                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full">{row.type}</span>
                                            </td>
                                            <td className="px-6 py-4 border-r text-center text-sm font-black text-gray-800 font-mono tracking-tighter">₹ {parseFloat(row.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            <td className="px-6 py-4 border-r text-center text-[11px] font-black text-blue-600 font-mono italic">{row.transactionId}</td>
                                            <td className="px-6 py-4 border-r text-center text-xs font-bold text-gray-500">{row.date}</td>
                                            <td className="px-6 py-4 text-center print:hidden">
                                                <button 
                                                    onClick={() => navigate('/accountant/society-payment-details', { state: { userId: row.userId } })}
                                                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black px-5 py-2.5 rounded-2xl transition-all shadow-lg shadow-indigo-100 active:scale-95 uppercase tracking-widest border-2 border-white"
                                                >
                                                    Payment Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-20 text-center text-gray-300 font-black uppercase tracking-widest italic animate-pulse bg-gray-50/50">
                                            कोणतीही पेमेंट नोंदणी आढळली नाही.
                                        </td>
                                    </tr>
                                ) }
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AccountantSocPaymentList;
